import { useEffect, useState } from 'react';
import type { PlanDto } from '@shared/api-dto';
import { useSession } from '../auth/AuthProvider';
import { toOutcome } from '../api/errors';

export interface PlanChoiceOptions {
  /** Where Lemon Squeezy returns the browser after a paid checkout. */
  checkoutReturnUrl?: string;
  /** Called once the org is entitled AND the caller is seated (trial is instant; paid is not). */
  onEntitled?: () => void;
}

export interface PlanChoice {
  /** `null` until the catalogue arrives; the caller renders its own loading state. */
  plans: PlanDto[] | null;
  /** A user-facing sentence, already mapped from the API outcome. Never a raw error. */
  error: string | null;
  /** The plan id currently being acted on, or `null`. Every button disables while it is set. */
  busyPlan: string | null;
  /** `billing:checkout:create` — per the documented decision, only `owner` holds it. */
  mayBuy: boolean;
  choose: (plan: PlanDto) => Promise<void>;
}

/**
 * The plan-selection FLOW, with no opinion about how plans are drawn.
 *
 * WHY A HOOK AND NOT JUST A COMPONENT. There are now two plan screens: the platform's card list
 * (PlanSelector, which calculator and claro render) and tenders' comparison table. They differ
 * only in markup — the fetch, the owner gate, the trial-vs-checkout branch and the error mapping
 * are identical, and MUST stay identical: a checkout that gains a step or an outcome that gains a
 * message has to reach both screens, and a second copy of this logic is how one of them silently
 * keeps the old behaviour. This is that single copy.
 *
 * The buy/trial actions are gated on `billing:checkout:create`. An admin sees the plans but is told
 * to ask their owner — better than letting them click and collect a 403. (To let admins buy: add
 * that permission to `admin` in root-roles.ts; `mayBuy` then goes true and both screens grow their
 * buttons with no UI change. See TODO.md.)
 */
export function usePlanChoice(productId: string, options: PlanChoiceOptions = {}): PlanChoice {
  const { checkoutReturnUrl, onEntitled } = options;
  const { client, can, reload } = useSession();
  const [plans, setPlans] = useState<PlanDto[] | null>(null);
  const [busyPlan, setBusyPlan] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    client
      .plans(productId)
      .then((r) => setPlans(r.plans))
      .catch(() => setError('לא ניתן לטעון את התוכניות.'));
  }, [client, productId]);

  async function choose(plan: PlanDto) {
    setBusyPlan(plan.id);
    setError(null);
    try {
      if (plan.trialDays !== null) {
        // Trial is synchronous: the server mints the subscription AND seats the caller, so by the
        // time this resolves the user can actually use the product. No redirect, no card.
        await client.startTrial(plan.id);
        await reload();
        onEntitled?.();
        return;
      }
      // Paid: hand off to Lemon Squeezy. Entitlement arrives later, by webhook — see BillingReturn.
      const { url } = await client.checkout(plan.id, checkoutReturnUrl);
      window.location.assign(url);
    } catch (err) {
      const outcome = toOutcome(err);
      setError(
        outcome.kind === 'trial-used'
          ? // Says what happened AND what to do next. The server's own sentence is English and
            // would be shown verbatim to a Hebrew user, which is what this replaces.
            'כבר השתמשתם בהתנסות החינם של המוצר. בחרו תוכנית בתשלום כדי להמשיך.'
          : outcome.kind === 'conflict'
            ? outcome.message
            : outcome.kind === 'not-permitted'
              ? 'רק בעלים של הארגון יכולים לשנות את התוכנית.'
              : 'משהו השתבש. נסו שוב.',
      );
    } finally {
      setBusyPlan(null);
    }
  }

  return { plans, error, busyPlan, mayBuy: can('billing:checkout:create'), choose };
}
